import { gql, useLazyQuery } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { GetFieldRelationsInput, Query } from 'generated/graphql';
import { useCallback } from 'react';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';

export const FETCH_METADATA_FIELD_RELATIONS = gql`
  query getFieldRelations($tenantID: ID!, $source: String) {
    getFieldRelations(request: { tenantID: $tenantID, source: $source }) {
      relations {
        field1
        field2
      }
    }
  }
`;

interface FetchMetadataFieldRelationsOptions {
  onCompleted?: (data: Query) => void;
}

export const useFetchMetadataFieldRelations = ({
  onCompleted,
}: FetchMetadataFieldRelationsOptions = {}) => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();
  const { showErrorNotification } = useNotificationContext();

  const [getFieldRelationsQuery, { data, loading, called, error }] =
    useLazyQuery<Query, GetFieldRelationsInput>(
      FETCH_METADATA_FIELD_RELATIONS,
      {
        fetchPolicy: 'cache-and-network',
        onError: () => {
          showErrorNotification({
            contentComponent: 'Failed to fetch the field relations.',
            props: {},
          });
        },
        onCompleted,
      },
    );

  const status = apolloStatusToQueryStatus(loading, called, false, error);

  const getFieldRelations = useCallback(
    async (input?: Omit<GetFieldRelationsInput, 'tenantID'>) => {
      try {
        const fieldRelationsData = await getFieldRelationsQuery({
          variables: {
            tenantID,
            ...input,
          },
        });
        return fieldRelationsData;
      } catch {
        // do nothing, handled by onError
      }
    },
    [getFieldRelationsQuery, tenantID],
  );

  return [
    getFieldRelations,
    {
      data: data?.getFieldRelations?.relations,
      isLoading: IsLoading(status),
      status,
      error,
    },
  ] as const;
};
